import {
  Accordion,
  AttachmentBox,
  AttachmentBoxSize,
  AttachmentBoxTheme,
  Disclaimer,
  DisclaimerVariant,
  IconName,
  Markdown,
  TypographyElement,
  TypographyVariant,
} from "@elearning-platform/ui";
import React, { useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import { AppConfig, Theme } from "../../../../appConfig";
import { Color } from "../../../../theme";
import { PageDataContext } from "../page/page.context";

import {
  Container,
  Title,
  Subtitle,
  HelperText,
  CounterWrapper,
  CurrentSlide,
  Separator,
  Float,
  DisclaimerWrapper,
  BoxWithPadding,
  StyledTypography,
  AccordionWrapper,
  AttachmentBoxWrapper,
  Subtext,
  SubtextWrapper,
} from "./textBox.styled";

export const TextBox = ({
  isHeader,
  fluidHeight,
  onFloatClick = Function.prototype,
  isInteraction,
  callComplete,
  completed,
}) => {
  const { t } = useTranslation();
  const {
    title,
    description,
    helperText,
    additionalBgAnimSrc,
    currentSlideCounter,
    slidesOverallCounter,
    elearningContent,
    baseUrl,
  } = useContext(PageDataContext);

  const subtitleText = useMemo(() => (
    description
      ? description
        .replaceAll("#brand", Color.brandBasic)
        .replaceAll("#contentURL/files/", `${AppConfig.content}/assets/files/`)
        .replaceAll("#contentURL", AppConfig.content)
      : undefined
  ), [description]);

  useEffect(() => {
    if (!completed && isInteraction) {
      callComplete();
    }
  }, []);

  return (
    <Container
      $isModernLesson={Theme.ModernScrollLesson}
      $withBg={!isHeader}
      $fluidHeight={fluidHeight}
      $isInteraction={isInteraction}
    >
      {!!(!isHeader && !additionalBgAnimSrc) && (
        <CounterWrapper>
          <CurrentSlide>{currentSlideCounter}</CurrentSlide>
          {` (${slidesOverallCounter})`}
        </CounterWrapper>
      )}
      {!additionalBgAnimSrc && (
        <Title
          $big={isHeader}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title, { ADD_ATTR: ["target"] }) }}
        />
      )}
      {!!elearningContent.titleSupportingText && (
        <StyledTypography tag={TypographyElement.div} variant={TypographyVariant.TextMD}>
          <Markdown text={elearningContent.titleSupportingText} />
        </StyledTypography>
      )}
      {!!(title && !additionalBgAnimSrc) && <Separator />}
      {!!subtitleText && <Subtitle dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(subtitleText, { ADD_ATTR: ["target"] }) }} />}
      {!!elearningContent.disclaimerData && (
        <BoxWithPadding>
          <DisclaimerWrapper>
            <Disclaimer icon={IconName.Flare} title={elearningContent.disclaimerData.title} variant={DisclaimerVariant.Brand}>
              <Markdown text={elearningContent.disclaimerData.text} />
            </Disclaimer>
          </DisclaimerWrapper>
        </BoxWithPadding>
      )}
      {!!elearningContent.accordions && (
        <AccordionWrapper>
          {elearningContent.accordions.map((accordion, index) => (
            <Accordion
              buttonLabel={accordion.buttonLabel}
              content={accordion.content}
              headline={accordion.headline}
              image={accordion.image ? baseUrl + accordion.image : undefined}
              // eslint-disable-next-line react/no-array-index-key
              key={`${accordion.headline}_${index}`}
            />
          ))}
        </AccordionWrapper>
      )}
      {!!elearningContent.attachmentBoxData && (
        <AttachmentBoxWrapper>
          <AttachmentBox
            basicText={elearningContent.attachmentBoxData.basicText}
            externalFileName={elearningContent.attachmentBoxData.externalFileName}
            externalFilePath={baseUrl + elearningContent.attachmentBoxData.externalFilePath}
            shouldOpenInNewTab
            size={AttachmentBoxSize.Small}
            supportingText={elearningContent.attachmentBoxData.supportingText}
            theme={AttachmentBoxTheme.Dark}
          />
        </AttachmentBoxWrapper>
      )}
      {!!elearningContent.secondaryDescription && <Subtitle>{elearningContent.secondaryDescription}</Subtitle>}
      {!!elearningContent.subtext && (
        <SubtextWrapper tag={TypographyElement.div} variant={TypographyVariant.TextSM}>
          <Subtext text={elearningContent.subtext} />
        </SubtextWrapper>
      )}
      <HelperText>{helperText}</HelperText>
      {isHeader && (
        <Float
          currentSection={currentSlideCounter - 1}
          sectionsOverall={slidesOverallCounter}
          title={t("lesson.scrollContent.introduction")}
          onClick={onFloatClick}
        />
      )}
    </Container>
  );
};

TextBox.propTypes = {
  isHeader: PropTypes.bool,
  onFloatClick: PropTypes.func,
  isInteraction: PropTypes.bool,
  callComplete: PropTypes.func,
  completed: PropTypes.bool,
};
